/**
 * Data Coverage Report Export Service
 * Handles JSON generation and file export for data coverage reports
 */

import type {
  StudyMetricCoverageResponse,
  StudyMetricDetailResponse,
  MissingDataAnalysisResponse,
  MetricStatsResponse,
  ParticipantCoverageSummaryResponse,
} from "./dataCoverageService";

export interface StudySummaryJSON {
  metadata: {
    exportedAt: string;
    studyId: string;
    studyName: string;
    exportedBy?: string;
    jsonVersion: string;
  };
  summaryStats: {
    dateRange: {
      fromDate: string;
      endDate: string;
      totalDays: number;
    };
    participantsAnalyzed: number;
    avgCoveragePct: number;
    logs: Array<{
      timestamp: string;
      level: "info" | "warning" | "error";
      message: string;
    }>;
  };
  metricsOverview: Array<{
    metricName: string;
    metricKey: string;
    avgCoveragePct: number;
    minCoveragePct: number;
    maxCoveragePct: number;
  }>;
}

export interface StudyMetricDetailJSON {
  metadata: {
    exportedAt: string;
    studyId: string;
    studyName: string;
    jsonVersion: string;
  };
  metricDetails: Array<{
    metricName: string;
    metricKey: string;
    participantCount: number;
    participantsWithData: number;
    avgCoveragePct: number;
    statistics: {
      meanValue: number | null;
      maxValue: number | null;
      minValue: number | null;
    };
    coverageDistributionByCohort: Array<{
      range: string;
      participantCount: number;
      percentage: number;
    }>;
    lowCoverageParticipants: Array<{
      participantId: string;
      participantName: string;
      coveragePct: number;
      daysWithData: number;
      meanValue: number | null;
    }>;
    allParticipants?: Array<{
      participantId: string;
      participantName: string;
      coveragePct: number;
      daysWithData: number;
      minValue: number | null;
      meanValue: number | null;
      maxValue: number | null;
    }>;
  }>;
}

export interface ParticipantSummaryJSON {
  metadata: {
    exportedAt: string;
    studyId: string;
    participantId: string;
    participantName: string;
    jsonVersion: string;
  };
  summaryStats: {
    dateRange: {
      fromDate: string;
      endDate: string;
      totalDays: number;
    };
    deviceType: "fitbit" | "healthkit" | "both" | "none";
    metricsWithData: number;
    totalMetrics: number;
    avgCoveragePct: number;
    logs: Array<{
      timestamp: string;
      level: "info" | "warning" | "error";
      message: string;
    }>;
  };
  metricCoverage: Array<{
    metricName: string;
    metricKey: string;
    coveragePct: number;
    daysWithData: number;
    missingDataCount: number;
    firstDate: string | null;
    lastDate: string | null;
  }>;
}

export interface ParticipantMissingDataJSON {
  metadata: {
    exportedAt: string;
    studyId: string;
    participantId: string;
    participantName: string;
    jsonVersion: string;
  };
  missingDataAnalysis: {
    totalMissingDays: number;
    coveragePct: number;
    daysWithCoverage: number;
    daysWithoutCoverage: number;
    missingDateRanges: Array<{
      startDate: string;
      endDate: string;
      dayCount: number;
    }>;
    metricsAffected: string[];
    summary: {
      datesWithAnyRows: number;
      datesPresentNoRealData: number;
      datesWithNoDataMarker: number;
      placeholderRangeCount: number;
      noCoverageRangeCount: number;
    };
  };
}

export interface ParticipantMetricsStatsJSON {
  metadata: {
    exportedAt: string;
    studyId: string;
    participantId: string;
    participantName: string;
    jsonVersion: string;
  };
  metricsStatistics: Array<{
    metricName: string;
    metricKey: string;
    dateRange: {
      fromDate: string;
      endDate: string;
      totalDays: number;
    };
    statistics: {
      coveragePct: number;
      daysWithData: number;
      daysWithoutData: number;
      minValue: number | null;
      meanValue: number | null;
      maxValue: number | null;
    };
    aggregationPeriods: Array<{
      period: string;
      aggregationType: string;
      value: number | null;
      pointCount: number;
      coveragePct?: number;
      daysWithData?: number;
    }>;
  }>;
}

class DataCoverageReportExportService {
  /**
   * Generate Study Summary JSON from coverage response
   */
  generateStudySummaryJSON(
    data: StudyMetricCoverageResponse,
    studyName: string,
  ): StudySummaryJSON {
    const logs: StudySummaryJSON["summaryStats"]["logs"] = [];

    if (data.fetch) {
      logs.push({
        timestamp: new Date().toISOString(),
        level: "info" as const,
        message: data.fetch.statusMessage,
      });

      if (data.fetch.participantsWithFailedFitbitRefresh > 0) {
        logs.push({
          timestamp: new Date().toISOString(),
          level: "warning" as const,
          message: `${data.fetch.participantsWithFailedFitbitRefresh} participant(s) had failed Fitbit refresh`,
        });
      }
    }

    return {
      metadata: {
        exportedAt: new Date().toISOString(),
        studyId: data.study.studyId,
        studyName: studyName,
        jsonVersion: "1.0",
      },
      summaryStats: {
        dateRange: {
          fromDate: data.range.fromDate,
          endDate: data.range.endDate,
          totalDays: data.range.totalDays,
        },
        participantsAnalyzed: data.summary.participantsAnalyzed,
        avgCoveragePct: Number(data.summary.avgCoveragePct.toFixed(2)),
        logs,
      },
      metricsOverview: data.metrics.map((m) => ({
        metricName: m.metricLabel,
        metricKey: m.metricKey,
        avgCoveragePct: Number(m.avgCoveragePct.toFixed(2)),
        minCoveragePct: Number(m.minCoveragePct.toFixed(2)),
        maxCoveragePct: Number(m.maxCoveragePct.toFixed(2)),
      })),
    };
  }

  /**
   * Generate Study Metric Detail JSON
   */
  generateStudyMetricDetailJSON(
    data: StudyMetricDetailResponse,
    studyName: string,
  ): StudyMetricDetailJSON {
    if (!data.analysis) {
      return {
        metadata: {
          exportedAt: new Date().toISOString(),
          studyId: data.study.studyId,
          studyName: studyName,
          jsonVersion: "1.0",
        },
        metricDetails: [],
      };
    }

    const metric = data.analysis;

    return {
      metadata: {
        exportedAt: new Date().toISOString(),
        studyId: data.study.studyId,
        studyName: studyName,
        jsonVersion: "1.0",
      },
      metricDetails: [
        {
          metricName: metric.metricLabel,
          metricKey: metric.metricKey,
          participantCount: metric.participantCount,
          participantsWithData: metric.participantsWithData,
          avgCoveragePct: Number(metric.statistics.avgCoveragePct.toFixed(2)),
          statistics: {
            meanValue: metric.statistics.meanValue
              ? Number(metric.statistics.meanValue.toFixed(2))
              : null,
            maxValue: metric.statistics.maxValue
              ? Number(metric.statistics.maxValue.toFixed(2))
              : null,
            minValue: metric.statistics.minValue
              ? Number(metric.statistics.minValue.toFixed(2))
              : null,
          },
          coverageDistributionByCohort: metric.coverageDistribution.map(
            (cohort) => ({
              range: cohort.range,
              participantCount: cohort.count,
              percentage: Number(cohort.percentage.toFixed(2)),
            }),
          ),
          lowCoverageParticipants: metric.lowestCoverageParticipants
            .slice(0, 20)
            .map((p) => ({
              participantId: p.participantId,
              participantName: p.participantName,
              coveragePct: Number(p.coveragePct.toFixed(2)),
              daysWithData: p.daysWithData,
              meanValue: p.meanValue ? Number(p.meanValue.toFixed(2)) : null,
            })),
          ...(metric.participants.length <= 100 && {
            allParticipants: metric.participants.map((p) => ({
              participantId: p.participantId,
              participantName: p.participantName,
              coveragePct: Number(p.coveragePct.toFixed(2)),
              daysWithData: p.daysWithData,
              minValue: p.minValue ? Number(p.minValue.toFixed(2)) : null,
              meanValue: p.meanValue ? Number(p.meanValue.toFixed(2)) : null,
              maxValue: p.maxValue ? Number(p.maxValue.toFixed(2)) : null,
            })),
          }),
        },
      ],
    };
  }

  /**
   * Generate Participant Summary JSON
   */
  generateParticipantSummaryJSON(
    data: ParticipantCoverageSummaryResponse,
  ): ParticipantSummaryJSON {
    const logs: ParticipantSummaryJSON["summaryStats"]["logs"] = [];

    if (data.fetch) {
      logs.push({
        timestamp: new Date().toISOString(),
        level: "info" as const,
        message: data.fetch.statusMessage,
      });

      if (
        data.fetch.failedFitbitRefreshRanges &&
        data.fetch.failedFitbitRefreshRanges.length > 0
      ) {
        logs.push({
          timestamp: new Date().toISOString(),
          level: "warning" as const,
          message: `Failed to refresh Fitbit for ${data.fetch.failedFitbitRefreshRanges.length} date range(s)`,
        });
      }
    }

    return {
      metadata: {
        exportedAt: new Date().toISOString(),
        studyId: "", // Will be passed from parent
        participantId: data.participant.participantId,
        participantName: data.participant.participantName,
        jsonVersion: "1.0",
      },
      summaryStats: {
        dateRange: {
          fromDate: data.range.fromDate,
          endDate: data.range.endDate,
          totalDays: data.range.totalDays,
        },
        deviceType: data.participant.deviceType,
        metricsWithData: data.summary.metricsWithData,
        totalMetrics: data.summary.totalMetrics,
        avgCoveragePct: Number(data.summary.avgCoveragePct.toFixed(2)),
        logs,
      },
      metricCoverage: data.metrics.map((metric) => ({
        metricName: metric.metricLabel,
        metricKey: metric.metricKey,
        coveragePct: Number(metric.coveragePct.toFixed(2)),
        daysWithData: metric.daysWithData,
        missingDataCount: metric.missingDataCount,
        firstDate: metric.firstDate,
        lastDate: metric.lastDate,
      })),
    };
  }

  /**
   * Generate Participant Missing Data Analysis JSON
   */
  generateParticipantMissingDataJSON(
    data: MissingDataAnalysisResponse,
  ): ParticipantMissingDataJSON {
    const missingRanges = [
      ...(data.noCoverageDateRanges || []),
      ...(data.placeholderDateRanges || []),
    ].slice(0, 50); // Limit to 50 ranges

    return {
      metadata: {
        exportedAt: new Date().toISOString(),
        studyId: "",
        participantId: data.participant.participantId,
        participantName: data.participant.participantName,
        jsonVersion: "1.0",
      },
      missingDataAnalysis: {
        totalMissingDays: data.summary.daysWithoutCoverage,
        coveragePct: Number(data.summary.coveragePct.toFixed(2)),
        daysWithCoverage: data.summary.daysWithCoverage,
        daysWithoutCoverage: data.summary.daysWithoutCoverage,
        missingDateRanges: missingRanges.map((r) => ({
          startDate: r.startDate,
          endDate: r.endDate,
          dayCount: r.dayCount,
        })),
        metricsAffected: data.keyMetrics || [],
        summary: {
          datesWithAnyRows: data.summary.datesWithAnyRows,
          datesPresentNoRealData: data.summary.datesPresentNoRealData,
          datesWithNoDataMarker: data.summary.datesWithNoDataMarker,
          placeholderRangeCount: data.summary.placeholderRangeCount,
          noCoverageRangeCount: data.summary.noCoverageRangeCount,
        },
      },
    };
  }

  /**
   * Generate Participant Metrics Statistics JSON
   */
  generateParticipantMetricsStatsJSON(
    metricsData: MetricStatsResponse[],
    participantId: string,
    participantName: string,
  ): ParticipantMetricsStatsJSON {
    return {
      metadata: {
        exportedAt: new Date().toISOString(),
        studyId: "",
        participantId,
        participantName,
        jsonVersion: "1.0",
      },
      metricsStatistics: metricsData.map((metric) => ({
        metricName: metric.metric.label,
        metricKey: metric.metric.key,
        dateRange: {
          fromDate: metric.range.fromDate,
          endDate: metric.range.endDate,
          totalDays: metric.range.totalDays,
        },
        statistics: {
          coveragePct: Number(metric.statistics.coveragePct.toFixed(2)),
          daysWithData: metric.statistics.daysWithData,
          daysWithoutData: metric.statistics.daysWithoutData,
          minValue: metric.statistics.minValue
            ? Number(metric.statistics.minValue.toFixed(2))
            : null,
          meanValue: metric.statistics.meanValue
            ? Number(metric.statistics.meanValue.toFixed(2))
            : null,
          maxValue: metric.statistics.maxValue
            ? Number(metric.statistics.maxValue.toFixed(2))
            : null,
        },
        aggregationPeriods: metric.aggregation.periods.map((p) => ({
          period: p.label,
          aggregationType: metric.aggregation.type,
          value: (p.meanValue && Number(p.meanValue.toFixed(2))) || null,
          pointCount: p.daysWithData + p.daysWithoutData,
          coveragePct: Number(p.coveragePct.toFixed(2)),
          daysWithData: p.daysWithData,
        })),
      })),
    };
  }
}

export const dataCoverageReportExportService =
  new DataCoverageReportExportService();
